import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import LightBackground from '@/components/LightBackground';
import { 
  ArrowLeft, 
  Save, 
  Loader2,
  Link2,
  DollarSign,
  ExternalLink,
  RefreshCw,
  Send
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface Setting {
  id: string;
  key: string;
  value: string;
  description: string | null;
  updated_at: string;
}

const AdminSettings = () => {
  const navigate = useNavigate();
  const { user, role, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Setting[]>([]);
  const [editedSettings, setEditedSettings] = useState<Record<string, string>>({});
  const [testingTelegram, setTestingTelegram] = useState(false);

  const handleTestTelegram = async () => {
    setTestingTelegram(true);
    try {
      const { data, error } = await supabase.functions.invoke('telegram-notify', {
        body: {
          text:
            `✅ <b>Test Notification</b>\n` +
            `Sent from Admin Settings at ${new Date().toLocaleString('en-US')}\n\n` +
            `If you see this in your Telegram group, the bot token and Chat ID are configured correctly.`,
        },
      });
      if (error) throw error;
      if (data?.ok === false) {
        toast.error(`Telegram not configured: ${data.skipped || data.error || 'unknown error'}`);
      } else {
        toast.success('Test notification sent to Telegram!');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to send test notification');
    } finally {
      setTestingTelegram(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      }
    }
  }, [user, role, authLoading, navigate]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('*')
        .order('key');

      if (error) throw error;
      setSettings(data || []);
      
      // Initialize edited settings with current values
      const initial: Record<string, string> = {};
      (data || []).forEach(s => {
        initial[s.key] = s.value;
      });
      setEditedSettings(initial);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user && role === 'admin') {
      loadSettings();
    }
  }, [user, role]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(editedSettings).map(([key, value]) => ({
        key,
        value,
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('admin_settings')
          .update({ value: update.value })
          .eq('key', update.key);
        
        if (error) throw error;
      }

      toast.success('Settings saved successfully!');
      await loadSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const hasChanges = () => {
    return settings.some(s => editedSettings[s.key] !== s.value);
  };

  const getSettingIcon = (key: string) => {
    if (key.includes('link')) return Link2;
    if (key.includes('price')) return DollarSign;
    return Link2;
  };

  const getSettingLabel = (key: string) => {
    switch (key) {
      case 'stripe_monthly_link': return 'Monthly Plan Stripe Link';
      case 'stripe_lifetime_link': return 'Lifetime Plan Stripe Link';
      case 'monthly_price': return 'Monthly Plan Price (USD)';
      case 'lifetime_price': return 'Lifetime Plan Price (USD)';
      default: return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header */}
      <motion.header
        className="relative z-20 p-6 flex justify-between items-center border-b border-border bg-white/80 backdrop-blur-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <span className="text-lg font-semibold text-foreground">
            Faceless Aura <span className="text-primary">Club</span>
          </span>
          <div className="h-6 w-px bg-border" />
          <span className="text-sm font-medium text-primary">Settings</span>
        </div>
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-muted-foreground hover:text-foreground"
            onClick={loadSettings}
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
          <span className="text-sm text-muted-foreground">{user?.email}</span>
        </div>
      </motion.header>

      {/* Main content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2">
            <span className="text-foreground">Admin Settings</span>
          </h1>
          <p className="text-muted-foreground text-sm">
            Manage payment links and pricing configuration
          </p>
        </motion.div>

        {/* Payment Links Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="border border-border bg-white rounded-2xl shadow-sm overflow-hidden mb-6"
        >
          <div className="p-6 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Link2 className="w-4 h-4 text-primary" />
              </div>
              Stripe Payment Links
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Configure your Stripe checkout links for Monthly and Lifetime plans
            </p>
          </div>

          <div className="p-6 space-y-6">
            {settings
              .filter(s => s.key.includes('link'))
              .map((setting) => {
                const Icon = getSettingIcon(setting.key);
                return (
                  <div key={setting.id} className="space-y-2">
                    <Label className="text-sm text-foreground flex items-center gap-2">
                      <Icon className="w-4 h-4 text-primary" />
                      {getSettingLabel(setting.key)}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={editedSettings[setting.key] || ''}
                        onChange={(e) => setEditedSettings({ 
                          ...editedSettings, 
                          [setting.key]: e.target.value 
                        })}
                        className="bg-slate-50 border-border font-mono text-sm"
                        placeholder="https://buy.stripe.com/..."
                      />
                      {editedSettings[setting.key] && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => window.open(editedSettings[setting.key], '_blank')}
                          className="shrink-0"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    {setting.description && (
                      <p className="text-xs text-muted-foreground">{setting.description}</p>
                    )}
                    {editedSettings[setting.key] !== setting.value && (
                      <p className="text-xs text-amber-600">Unsaved changes</p>
                    )}
                  </div>
                );
              })}
          </div>
        </motion.div>

        {/* Pricing Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="border border-border bg-white rounded-2xl shadow-sm overflow-hidden mb-6"
        >
          <div className="p-6 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-green-600" />
              </div>
              Pricing Configuration
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Set the display prices for your membership plans
            </p>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {settings
              .filter(s => s.key.includes('price'))
              .map((setting) => (
                <div key={setting.id} className="space-y-2">
                  <Label className="text-sm text-foreground">
                    {getSettingLabel(setting.key)}
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={editedSettings[setting.key] || ''}
                      onChange={(e) => setEditedSettings({ 
                        ...editedSettings, 
                        [setting.key]: e.target.value 
                      })}
                      className="bg-slate-50 border-border pl-8"
                    />
                  </div>
                  {editedSettings[setting.key] !== setting.value && (
                    <p className="text-xs text-amber-600">Unsaved changes</p>
                  )}
                </div>
              ))}
          </div>
        </motion.div>

        {/* Telegram Test Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="border border-border bg-white rounded-2xl shadow-sm overflow-hidden mb-6"
        >
          <div className="p-6 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Send className="w-4 h-4 text-blue-600" />
              </div>
              Telegram Notifications
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Send a test message to verify your bot token and Chat ID are configured correctly.
              You'll get a Telegram message for every sale and project request.
            </p>
          </div>
          <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-xs text-muted-foreground">
              Secrets used: <code className="font-mono">TELEGRAM_BOT_TOKEN</code>, <code className="font-mono">TELEGRAM_CHAT_ID</code>
            </div>
            <Button
              onClick={handleTestTelegram}
              disabled={testingTelegram}
              className="flex items-center gap-2"
            >
              {testingTelegram ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send Test Notification
            </Button>
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex justify-end gap-4"
        >
          <Button
            variant="outline"
            onClick={() => {
              const initial: Record<string, string> = {};
              settings.forEach(s => {
                initial[s.key] = s.value;
              });
              setEditedSettings(initial);
            }}
            disabled={!hasChanges() || saving}
          >
            Reset Changes
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges() || saving}
            className="flex items-center gap-2"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Settings
          </Button>
        </motion.div>

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-8 p-4 border border-blue-200 bg-blue-50 rounded-2xl"
        >
          <h3 className="text-sm font-medium text-blue-800 mb-2">How Payment Links Work</h3>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• These links are used on the /apply page for new member checkout</li>
            <li>• Create links in your Stripe Dashboard → Payment Links</li>
            <li>• The links should redirect to /payment-success after successful payment</li>
            <li>• Changes take effect immediately after saving</li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminSettings;
